// ManageSshAccessModal — review and revoke who currently has SSH access to a
// host, split out from ProvisionSshSandboxModal (2026-09-10) because "create
// a sandbox for someone" and "review/revoke existing access" are genuinely
// different jobs. They used to share one modal, with this content trailing
// below the create-flow's own Cancel/Review buttons — an admin who opened
// that modal specifically to revoke someone had to scroll past an entire
// unrelated form to find it, with nothing about that button's name hinting
// revocation lived there too. This modal only reviews/revokes; use the host
// card's "Create SSH Sandbox" button to add someone.
import { useEffect, useState } from "react";
import { Modal, Popconfirm, Button, Alert, Empty } from "antd";
import { KeyRound, Trash2 } from "lucide-react";
import { fleetApi, type AccessGrant } from "@/lib/api/fleet";
import { Tag } from "@/shared/ui";
import { PolicyChip } from "./PolicyChip";
import type { ProvisionHost } from "./ProvisionSshSandboxModal";

const MONO = "var(--font-mono, 'JetBrains Mono Variable', monospace)";

export default function ManageSshAccessModal({ open, onClose, onChanged, hosts, hostId }: {
  open: boolean;
  onClose: () => void;
  onChanged?: () => void;
  hosts: ProvisionHost[];
  // Always host-scoped — the only entry point is a specific host's own kebab
  // menu (HostCard), unlike ProvisionSshSandboxModal's now-vestigial
  // optional lockedHostId.
  hostId: string;
}) {
  const [node, setNode] = useState("");
  const [tiers, setTiers] = useState<string[]>([]);
  const [grants, setGrants] = useState<AccessGrant[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const selectedHost = hosts.find((h) => h.id === hostId);
  const tier = tiers[0] ?? selectedHost?.policyTier;

  useEffect(() => {
    if (!open || !selectedHost?.ip) return;
    setErr(null);
    fleetApi.access.list(selectedHost.ip).then((r) => {
      setNode(r.node); setTiers(r.tiers || []); setGrants(r.grants || []);
    }).catch((e) => setErr(e instanceof Error ? e.message : "Couldn't load access"));
    // Only re-fetch when the modal opens, same reasoning as the grant modal:
    // a poll tick on FleetPage shouldn't yank the list out from under a
    // read admin mid-review.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hostId]);

  const revoke = async (p: string) => {
    try {
      await fleetApi.access.revoke(p, node || undefined);
      onChanged?.();
      fleetApi.access.list(selectedHost?.ip || "").then((r) => setGrants(r.grants || []));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Revoke failed");
    }
  };

  return (
    <Modal open={open} onCancel={onClose} width={640} footer={null} destroyOnHidden
      title={
        <span className="inline-flex items-center gap-2">
          <KeyRound size={17} /> Manage SSH Access
        </span>
      }>
      {err && <Alert type="error" showIcon className="mb-3" message={err} />}

      {selectedHost && (
        <div className="mb-3 grid grid-cols-3 gap-4 border-b border-border pb-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">Host</div>
            <div className="text-[13px] text-text-primary" style={{ fontFamily: MONO }}>{selectedHost.name}</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">Policy</div>
            <div className="mt-0.5">
              {tier ? <PolicyChip tier={tier} linkable={false} /> : <span className="text-[13px] text-text-muted">—</span>}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">Slots remaining</div>
            <div className="text-[13px] text-text-primary">
              {Math.max(selectedHost.capacity - selectedHost.used, 0)} of {selectedHost.capacity} free
            </div>
          </div>
        </div>
      )}

      <p className="mb-3 text-sm text-text-secondary">
        Everyone who can SSH into this host, and where their access comes from. To add someone, use
        <strong> Create SSH Sandbox</strong> from the host card.
      </p>

      {grants.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No grants yet" />
      ) : (
        <div className="flex flex-col gap-1.5">
          {grants.map((g) => {
            // An oidc-cert grant whose principal is a bare "*" or "*@domain"
            // isn't one person — it's fleetd's blanket pre-authorization ("anyone
            // proven by SSO from this domain gets a cert here"), a materially
            // bigger-consequence grant than a single named ssh-key. Showing the
            // raw kind/principal ("oidc-cert" / "*") communicated nothing; say
            // what it actually means instead.
            const wildcard = g.kind === "oidc-cert" && g.principal.startsWith("*");
            const domain = g.principal.startsWith("*@") ? g.principal.slice(2) : null;
            const label = wildcard ? (domain ? `Anyone from @${domain}` : "Anyone signing in via SSO") : (g.owner || g.principal);
            return (
              <div key={g.principal} className="flex items-center gap-2 rounded-lg border border-border bg-surface-elev px-3 py-1.5">
                <Tag color={g.kind === "ssh-key" ? "gold" : "blue"}>{g.kind === "ssh-key" ? "SSH key" : "SSO"}</Tag>
                <span
                  className="min-w-0 flex-1 truncate text-[12.5px] text-text-primary"
                  style={wildcard ? undefined : { fontFamily: MONO }}
                  title={g.principal}
                >
                  {label}
                </span>
                {/* Non-clickable, same as the Invite via SSO modal: a navigation
                    link inside an open modal abandons its context awkwardly. */}
                {g.tier && <PolicyChip tier={g.tier} linkable={false} />}
                <Popconfirm
                  title="Revoke this access?"
                  description={wildcard
                    ? "Removes automatic SSO access for this domain — everyone using it will need an explicit grant instead."
                    : "Removes the trust line (and the key, for ssh-key grants)."}
                  okText="Revoke" okButtonProps={{ danger: true }} onConfirm={() => revoke(g.principal)}
                >
                  <Button size="small" danger type="text" icon={<Trash2 size={14} />} />
                </Popconfirm>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
